import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import LoadingComponent from "../../components/common/LoadingComponent";
import { getAssistant } from "../../features/assistant/assistantSlice";
import {
  fetchSettingById,
  upsertSetting,
} from "../../features/settings/settingsSlice";
import {
  fetchNotionDatabases,
} from "../../features/oauth/oauthAPI";
import {
  getWorkspaceId,
  getWorkspaceName,
  getItemId,
} from "../AssistantDetails/Assistants.utils";
import { getIcon } from "../../constants/constants";
import {
  buildSettingPayload,
  isThreeStepPlatform,
  ITEMS_FETCHERS,
  loadThirdStepItems,
  normalize,
  PLATFORM_OPTIONS,
  WORKSPACE_FETCHERS,
} from "../OAuth/PlatformWorkspace.utils";
import { syncUserData } from "../../features/user/userAPI";

function Projects() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { assistantId, settingId } = useParams();
  const { setting } = useSelector((state) => state.setting);

  const [assistant, setAssistant] = useState(null);
  const [platformName, setPlatformName] = useState("");
  const [workspaceLoading, setWorkspaceLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  const [saving, setSaving] = useState(false);

  const platformCfg = PLATFORM_OPTIONS[platformName] || { preSteps: [] };
  const isDatabaseOnly = platformCfg.isDatabaseOnly || false;

  const middleSectionLabel = (() => {
    if (isDatabaseOnly) return "Select Database";
    if (isThreeStepPlatform(platformName)) return "Select Project";
    return (
      platformCfg.preSteps.find((s) => s.key === "item")?.label ||
      "Select Project"
    );
  })();

  const headerTitle = (() => {
    if (isDatabaseOnly) return "Select Database";
    if (isThreeStepPlatform(platformName))
      return "Select Workspace, Project & Board";
    return "Select Workspace & Project";
  })();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await dispatch(getAssistant(assistantId));
        const data = res.payload;
        setAssistant(data);
        setPlatformName(data?.platform?.type?.toLowerCase() || "");
        if (settingId) {
          await dispatch(fetchSettingById({ settingUuid: settingId }));
        }
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [assistantId, settingId, dispatch]);

  useEffect(() => {
    const loadWorkspaces = async () => {
      if (!platformName || !setting?.uuid) return;

      try {
        const cfg = PLATFORM_OPTIONS[platformName];

        if (cfg?.isDatabaseOnly) {
          const res = await fetchNotionDatabases(setting.uuid);
          setItems(res?.data || []);
          setWorkspaceLoading(false);
          return;
        }

        const workspaceFetcher = WORKSPACE_FETCHERS[platformName];
        if (workspaceFetcher) {
          const res = await workspaceFetcher(setting.uuid, assistantId);
          setWorkspaces(res?.data || []);
        }
      } catch (err) {
        console.error(err);
      }

      setWorkspaceLoading(false);
    };

    loadWorkspaces();
  }, [platformName, setting?.uuid, assistantId]);

  const fetchItems = async (workspaceId) => {
    const itemsFetcher = ITEMS_FETCHERS[platformName];
    if (!itemsFetcher) return;

    try {
      const res = await itemsFetcher(setting.uuid, workspaceId);
      if (isThreeStepPlatform(platformName)) {
        setProjects(res?.data || []);
      } else {
        setItems(res?.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleWorkspaceClick = async (id) => {
    setSelectedWorkspace(id);
    setSelectedProject("");
    setSelectedItem("");
    setProjects([]);
    setItems([]);
    setProjectsLoading(true);
    await fetchItems(id);
    setProjectsLoading(false);
  };

  const handleProjectClick = async (id) => {
    setSelectedProject(id);
    if (isThreeStepPlatform(platformName)) {
      setSelectedItem("");

      const data = await loadThirdStepItems({
        platformName,
        settingUuid: setting.uuid,
        workspaceId: selectedWorkspace,
        projectId: id,
        projects,
      });

      setItems(data);

      return;
    }

    setSelectedItem(id);
  };

  const handleSave = async () => {
    if (saving) return;
    try {
      setSaving(true);
      const payload = buildSettingPayload({
        setting,
        selectedWorkspace: isDatabaseOnly ? "" : selectedWorkspace,
        selectedProject,
        selectedItem,
        workspaces,
        projects,
        items,
        settingId,
        platformName,
      });

      await dispatch(upsertSetting({ uuid: assistantId, data: payload }));
      await syncUserData({ setting: setting?.uuid });
      navigate(`/onboarding/apps`);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const workspaceOptions = workspaces.map((w) => ({
    id: String(getWorkspaceId(w, platformName) ?? w.id),
    label: getWorkspaceName(w, platformName) ?? w.name,
  }));

  const middleOptions = isThreeStepPlatform(platformName)
    ? projects.map((p) => ({ id: String(p.id), label: p.name }))
    : items.map((i) => ({
        id: isDatabaseOnly
          ? String(i.id || i.database_id)
          : String(getItemId(i, platformName)),
        label: i.name,
      }));

  const isSaveDisabled =
    saving ||
    (!isDatabaseOnly && !selectedWorkspace) ||
    (middleOptions.length > 0 &&
      !(isThreeStepPlatform(platformName) ? selectedProject : selectedItem)) ||
    (isThreeStepPlatform(platformName) && items.length > 0 && !selectedItem);

  const iconSrc = getIcon(platformName);

  const renderOptionRow = (opt, isSelected, onClick) => (
    <div
      key={String(opt.id)}
      className="ws-option-item"
      onClick={onClick}
      style={{
        border: isSelected ? "2px solid #2dbe76" : "0.5px solid",
        cursor: "pointer",
      }}
    >
      <div
        className={`rounded-circle d-flex align-items-center justify-content-center border ${
          isSelected
            ? "bg-success border-success text-white"
            : "border-secondary"
        }`}
        style={{ width: 22, height: 22 }}
      >
        {isSelected && <span>✓</span>}
      </div>
      <span className="ws-option-label">{opt.label}</span>
    </div>
  );

  return (
    <div className="container py-5 mt-4" style={{ maxWidth: "1400px" }}>
      <div className="card">
        <div className="ws-card-header px-4 py-3 d-flex align-items-center gap-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center bg-body-emphasis"
            style={{ width: 50, height: 50 }}
          >
            <img src={iconSrc} alt="" style={{ width: 22, height: 22 }} />
          </div>
          <div>
            <p className="ws-step-heading mb-1">{headerTitle}</p>
            <h5 className="mb-0 fw-bold">{assistant?.name}</h5>
          </div>
        </div>

        <div className="px-4 py-4 ws-panel">
          {!isDatabaseOnly && (
            <div className="mb-4">
              <p className="ws-label">Select Workspace</p>

              {workspaceLoading ? (
                <div className="d-flex justify-content-center py-4">
                  <LoadingComponent loading size={14} />
                </div>
              ) : (
                <div className="ws-option-list">
                  {workspaceOptions.map((opt) => {
                    const isSelected =
                      normalize(selectedWorkspace) === normalize(opt.id);
                    return renderOptionRow(opt, isSelected, () =>
                      handleWorkspaceClick(String(opt.id)),
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {(selectedWorkspace || isDatabaseOnly) && (
            <div className="mt-4">
              <p className="ws-label">{middleSectionLabel}</p>

              {projectsLoading || workspaceLoading ? (
                <div className="d-flex justify-content-center py-4">
                  <LoadingComponent loading size={14} />
                </div>
              ) : middleOptions.length === 0 ? (
                <p className="text-center py-3 mb-0 text-muted">
                  No projects available.
                </p>
              ) : (
                <div className="ws-option-list">
                  {middleOptions.map((opt) => {
                    const isSelected =
                      normalize(
                        isThreeStepPlatform(platformName)
                          ? selectedProject
                          : selectedItem,
                      ) === normalize(opt.id);
                    return renderOptionRow(opt, isSelected, () =>
                      handleProjectClick(String(opt.id)),
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {isThreeStepPlatform(platformName) && selectedProject && (
            <div className="mt-4">
              <p className="ws-label">Select Board</p>
              <div className="ws-option-list">
                {items.map((board) => {
                  const opt = { id: String(board.id), label: board.name };
                  const isSelected =
                    normalize(selectedItem) === normalize(board.id);
                  return renderOptionRow(opt, isSelected, () =>
                    setSelectedItem(String(board.id)),
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="ws-card-footer px-4 py-3 d-flex justify-content-end">
          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            disabled={isSaveDisabled}
            onClick={handleSave}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm" />
                Saving...
              </>
            ) : (
              "Save & Continue →"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Projects;
